/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { SxProps, Theme } from '@mui/material';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

export const loginContainerSX: SxProps<Theme> = (theme) => ({
	p: '0',
	height: '100%',
	display: 'flex',
	borderRadius: '0',
	[theme.breakpoints.down('md')]: {
		flexDirection: 'column',
	},
});

export const loginContainerDrawerSX: SxProps<Theme> = () => ({
	boxShadow: 'none',
});

export const loginFormContainerSX = (theme: Theme, isDialogView: boolean): SxProps => ({
	padding: !isDialogView ? '20px 40px' : '25px 20px',
	[theme.breakpoints.up(960)]: {
		width: !isDialogView ? '36%' : '100%',
	},
});

export const loginFormContainerDrawerSX = (): SxProps => ({
	padding: '5px 10px',
	overflowY: 'scroll',
});

export const espotContainerSX: SxProps<Theme> = (theme) => ({
	display: 'flex',
	alignItems: 'center',
	backgroundColor: 'white',
	padding: '20px',
	[theme.breakpoints.up(960)]: {
		width: '64%',
	},
	[theme.breakpoints.up('md')]: {
		borderLeft: '1px solid',
		borderLeftColor: 'backgroundGray',
	},
	'& .MuiStack-root': {
		[theme.breakpoints.down(960)]: {
			margin: '60px 0',
		},
	},
});

export const loginInputSx: SxProps<Theme> = {
	'& .MuiInputLabel-root.Mui-error': {
		color: 'grey.200',
	},
	'MuiInputLabel-root.Mui-error': {
		color: 'grey.200 !important',
	},
	'.MuiInputLabel-formControl, .Mui-error': {
		color: 'grey.200 !important',
	},
	my: '10px',
	'& .MuiInputBase-input': {
		fontSize: '12px',
		p: '10px',
	},
	'& .MuiInputLabel-root': {
		top: -6,
		color: 'grey.200',
		fontSize: '15px',
		mb: 0,
	},
	'& .MuiInputLabel-shrink': {
		top: 0,
		color: 'darkgray !important',
		fontSize: '14px',
	},
	'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
		borderColor: 'primary.main',
	},
	'& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
		borderColor: '#0000003b',
		borderBottomColor: 'error.main',
		color: 'black',
	},
};

export const loginInputDrawerSx: SxProps<Theme> = {
	mt: '5px',
	mb: 0,
	p: 0,
};

export const LoginSx: Record<string, SxProps<Theme>> = {
	titleContainer: {
		marginBottom: '5px',
	},
	title: {
		alignSelf: 'start',
		fontWeight: OpenSansFontWeights.light,
		color: 'grey.500',
		width: 'fit-content',
		borderBottom: '2px solid',
		borderBottomColor: 'primary.main',
		marginBottom: '5px',
		fontSize: '27px',
		whiteSpace: 'break-spaces',
		'-webkit-box-align': 'baseline',
		alignItems: 'baseline',
	},
	titleDivider: {
		flexGrow: '1',
		marginBottom: '5px',
		borderColor: 'grey.50',
	},
	button: {
		p: '10px',
		mb: '10px',
		fontSize: '16px',
		fontWeight: OpenSansFontWeights.bold,
	},
	buttonOutlined: {
		p: '10px',
		mb: '10px',
		fontSize: '16px',
		fontWeight: OpenSansFontWeights.bold,
		border: '1px solid',
		'&:hover': {
			backgroundColor: 'primary.main',
			color: 'white',
			border: '1px solid',
			borderRadius: '3px',
		},
	},
	text: {
		color: 'grey.200',
		mb: '10px',
	},
	link: {
		color: 'secondary.main',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	inlineLink: {
		color: 'secondary.main',
		textDecoration: 'none',
		'&:hover': {
			color: 'secondary.main',
			textDecoration: 'none',
		},
	},
	switchLabel: {
		'& .MuiFormControlLabel-label': {
			fontWeight: OpenSansFontWeights.regular,
			color: 'grey.500',
			fontSize: '12px',
		},
		'& .MuiSwitch-track': {
			backgroundColor: 'grey.200',
			opacity: '1 !important',
		},
		'& .MuiSwitch-thumb': {
			color: 'grey.100',
		},
		py: '5px',
	},
	noAccountLabel: {
		color: 'grey.500',
		fontWeight: OpenSansFontWeights.semibold,
		mt: '15px',
		mb: '10px',
		py: '10px',
	},
	divider: {
		my: '10px',
	},
	forgotPasswordText: {
		my: '20px',
	},
	passwordInput: {
		p: '2.5px',
		mb: '0',
		...loginInputSx,
	},
	helperText: { ml: '0', fontSize: '10px' },
};

export const LoginDrawerSx: Record<string, SxProps<Theme>> = {
	titleContainer: {
		marginBottom: '0',
	},
	title: {
		fontWeight: OpenSansFontWeights.semibold,
		borderBottom: '0',
		marginBottom: '0',
		fontSize: '18px',
		fontFamily: 'HelveticaNeue_THD',
		textTransform: 'uppercase',
	},
	titleDivider: {
		display: 'none',
	},
	inlineLink: {
		fontSize: '12px',
		'&:hover': {
			textDecoration: 'underline',
			textDecorationColor: 'secondary.main',
			cursor: 'pointer',
		},
	},
	noAccountLabel: {
		mt: 0,
		mb: '5px',
		py: 0,
	},
	divider: {
		mt: 0,
	},
	forgotPasswordText: {
		my: '10px',
	},
};
export const changePasswordContainerSX: SxProps<Theme> = (theme) => ({
	width: '500px',
	[theme.breakpoints.down('md')]: {
		width: '100%',
	},
});
